// Discriminated union of every Mixpanel event the app emits. Event names
// are snake_case; properties are snake_case. Adding a new event = add a
// new variant here. Do not pass arbitrary `Dict`s to trackEvent — go
// through the useAnalytics() hook which is typed against this union.
//
// Convention: never put PII, free-form user text, or financial amounts
// in event properties. Bucket message length / counts; reference rows
// by id only. See the privacy callouts in the rollout plan.
//
// Property-type discipline: prefer constrained types (string-literal
// unions, template literal types, enums) over `string` wherever the
// runtime value set is closed. Where the value set is open at the
// language boundary (e.g. error codes minted by an evolving backend
// enum), the field stays typed as `string` and the call site is
// responsible for keeping the cardinality low — the comment on each
// such field describes the expected shape.

// Which table a row preview opened from: a list page, or a table inside a detail page.
export type PreviewList =
  | 'spending'
  | 'contracts'
  | 'compliance'
  | 'properties'
  | 'tickets'
  | 'ai_extracted'
  | 'document_review'
  | 'invoice_contracts'
  | 'invoice_elevators'
  | 'invoice_linked_invoices'
  | 'invoice_related_invoices'
  | 'invoice_tickets'
  | 'invoice_car_tickets'
  | 'elevator_contracts'
  | 'elevator_invoices'
  | 'elevator_tickets'
  | 'property_contracts'
  | 'property_elevators'
  | 'property_tickets'
  | 'property_invoices'
  | 'needs_attention'
  | 'contract_properties'
  | 'tasks'
  | 'property_tasks'
  | 'elevator_tasks';

export type AppEvent =
  // ─── Navigation ────────────────────────────────────────────────────
  | {
      name: 'page_viewed';
      properties: { path: string; route_id?: string };
    }

  // ─── Auth ──────────────────────────────────────────────────────────
  | { name: 'login_succeeded'; properties: { method: 'password' | 'oauth' | 'magic_link' } }
  // Forensics for failures live in Sentry; the analytics signal is just
  // the count of failed attempts. Intentionally no free-form `reason`.
  | { name: 'login_failed'; properties: Record<string, never> }
  | { name: 'signup_completed'; properties: { role?: string } }
  // Fired when a passwordless sign-in link is requested. Emitted whether or
  // not the address maps to a real account (the UI doesn't reveal which), so
  // this counts intent, not successful sends.
  | { name: 'magic_link_requested'; properties: Record<string, never> }
  | { name: 'password_reset_requested'; properties: Record<string, never> }
  | { name: 'password_reset_completed'; properties: Record<string, never> }
  | { name: 'logout'; properties: Record<string, never> }

  // ─── Chat — panel chrome + per-turn flow ───────────────────────────
  // Visibility of the chat FAB / panel is currently gated to consultants;
  // the render condition in OttoIntegration is the source of truth, so
  // these events automatically follow whatever the rollout rule is. A
  // `page_viewed`-based denominator would over-count once the rule
  // includes anything beyond role.
  | {
      name: 'chat_fab_impression';
      // Fired once per session, the first time the FAB renders for the
      // user. Ref-deduped in OttoIntegration; cleared on logout via
      // mixpanel.reset. `route` is the path the user was on at first
      // impression.
      properties: { route: string };
    }
  | {
      name: 'chat_fab_clicked';
      properties: { route: string };
    }
  | {
      // Panel transitions from closed → docked. `source` distinguishes
      // FAB-driven opens from keyboard shortcut or deep-link opens. The
      // LD flag value is kept on this event (not on `*_impression`) so
      // funnels can correlate panel adoption with flag rollouts.
      name: 'chat_panel_opened';
      properties: {
        source: 'fab' | 'shortcut' | 'deep_link';
        feature_flag_ai_chat: boolean;
      };
    }
  | {
      // Dock → full takeover. Fires only on the upgrade transition, not
      // on initial open in full mode (that's `chat_panel_opened` with
      // source='deep_link' for a full-mode deep link).
      name: 'chat_panel_expanded';
      properties: { session_id: string | null };
    }
  | {
      // Panel dismissed back to FAB. `mode` is what was active at close
      // time; `duration_ms` is the elapsed time since the corresponding
      // chat_panel_opened.
      name: 'chat_panel_closed';
      properties: {
        mode: 'dock' | 'full';
        duration_ms: number;
        session_id: string | null;
      };
    }
  | {
      // User picked a different conversation from the history panel.
      // `from_session_id` is null on the first switch after a fresh
      // panel open (no prior active session).
      name: 'chat_session_switched';
      properties: {
        from_session_id: string | null;
        to_session_id: string;
      };
    }
  | {
      // Unread indicator (red dot) cleared by opening the session it
      // belonged to. Re-engagement signal — distinct from a passive
      // dismissal (which doesn't fire anything).
      name: 'chat_unread_indicator_dismissed';
      properties: { session_id: string };
    }
  | {
      name: 'chat_session_created';
      properties: { session_id: string };
    }
  | {
      name: 'chat_example_question_clicked';
      properties: { question_title: string };
    }
  | {
      name: 'chat_message_sent';
      properties: {
        session_id: string;
        turn_index: number;
        message_length_bucket: 'xs' | 's' | 'm' | 'l' | 'xl';
      };
    }
  | {
      name: 'chat_first_token_received';
      properties: { session_id: string; turn_index: number; ttft_ms: number };
    }
  | {
      name: 'chat_tool_invoked';
      properties: {
        session_id: string;
        turn_index: number;
        tool_name: string;
      };
    }
  | {
      // result_status:'success' means the tool RETURNED, not that a card
      // painted — adapters/cards can render nothing (e.g. an empty
      // rule-suggestions card). For a true "card shown" denominator use the
      // card-specific event (e.g. rule_suggestion_card_shown), not this.
      name: 'chat_tool_result_rendered';
      properties: {
        session_id: string;
        turn_index: number;
        tool_name: string;
        result_status: 'success' | 'error';
      };
    }
  | {
      name: 'chat_message_completed';
      properties: {
        session_id: string;
        turn_index: number;
        duration_ms: number;
        tool_count: number;
        run_id: string | null;
      };
    }
  | {
      // `reason` distinguishes user-driven aborts (the explicit stop
      // button) from involuntary ones (the runner unmounting because
      // its session was deleted). The browser-close path can't reliably
      // fire from JS, so the backend Sentry `genui.chat.turn` metric
      // remains the source of truth for server-observed aborts.
      name: 'chat_stream_aborted';
      properties: {
        session_id: string;
        turn_index: number;
        elapsed_ms: number;
        reason: 'unmount' | 'stop_button';
      };
    }
  | {
      // Error forensics (stack, message, source) live in Sentry — the
      // analytics field is just a low-cardinality category string. We
      // do not enumerate the union here because the codes are minted by
      // services/api/genui/errors.py::ChatErrorCode plus a frontend
      // 'unknown' fallback; mirroring the Python enum in TS would add a
      // sync hazard without a compile-time guarantee at the language
      // boundary (the SSE payload is decoded from JSON). parseChatError
      // is the choke point that maps wire text → code.
      name: 'chat_error_displayed';
      properties: {
        session_id: string;
        turn_index: number;
        error_code?: string;
      };
    }
  | {
      // Fires on the export affordance in a chat report widget. `row_count`
      // is what actually landed in the file, which is NOT necessarily the
      // full result set: card lists are capped at 25 and grouped aggregates
      // at top-50 server-side, so a truncated export looks identical to a
      // complete one in the CSV itself.
      name: 'chat_export_csv';
      properties: {
        session_id?: string;
        block_type: string;
        row_count: number;
      };
    }
  | {
      name: 'chat_feedback_submitted';
      properties: {
        run_id: string;
        rating: 'thumbs_up' | 'thumbs_down';
        reasons: string[];
        feedback_key: string;
      };
    }

  // ─── Command Palette ───────────────────────────────────────────────
  | {
      name: 'command_palette_opened';
      properties: { trigger: 'keyboard' | 'menu' };
    }
  | {
      name: 'command_palette_search_executed';
      properties: { category: string; query_length: number; result_count: number };
    }
  | {
      name: 'command_palette_result_clicked';
      properties: { result_type: string; group: string };
    }
  | {
      name: 'command_palette_navigation_clicked';
      properties: { action_label: string };
    }

  // ─── Spending / Invoices ───────────────────────────────────────────
  | { name: 'spending_viewed'; properties: { tab?: string } }
  | { name: 'invoice_opened'; properties: { invoice_id: string } }
  // Fired when a recipient of the weekly invoice summary email clicks a
  // deep link and lands in the app. Captured from the `utm_*` params in
  // `_app.tsx` (the email can't run JS, so this in-app landing is the
  // click-through signal). Pairs with a subsequent `invoice_opened` within
  // a conversion window to measure whether the recap actually pulls clients
  // in to look at their spending. `link_kind` is which of the two email
  // links was followed (the invoices summary vs the proposals summary).
  | {
      name: 'weekly_invoice_email_visited';
      properties: { link_kind?: 'invoice' | 'proposal' };
    }
  | {
      // Action values are the lower-case form of the backend's
      // InvoiceActionType enum (APPROVE / REJECT / PARTIAL /
      // REQUEST_DATA / RESET) — keeping them aligned so analysts can
      // correlate Mixpanel events with backend audit logs directly.
      // `edit` is the frontend-only path where InvoiceForm saves a
      // change to an existing invoice (`dto.id` is set at save time).
      name: 'invoice_action_taken';
      properties: {
        invoice_id: string;
        action: 'approve' | 'reject' | 'partial' | 'request_data' | 'reset' | 'edit';
      };
    }
  | {
      // Post-finalize nudge offering to draft a contract review rule, shown
      // only when the consultant's decision disagrees with the AI review.
      // consultant_status is the consultant's finalized decision; ai_status is
      // Lift AI's recommendation (kept open since the AI has extra states like
      // pending-data / reset).
      name: 'rule_suggestion_nudge_shown';
      properties: {
        invoice_id: string;
        consultant_status: 'approved' | 'rejected' | 'partial-approval';
        ai_status: string;
      };
    }
  | {
      // session_id maps this invoice back to the chat the nudge opened — it's
      // the last event carrying invoice_id, so it bridges the invoice funnel to
      // the chat-side rule_suggestion_* events.
      name: 'rule_suggestion_nudge_clicked';
      properties: {
        invoice_id: string;
        session_id?: string;
        consultant_status?: 'approved' | 'rejected' | 'partial-approval';
        ai_status?: string;
      };
    }
  | {
      // Consultant explicitly dismissed the nudge — distinguishes a deliberate
      // decline from passively leaving it (the snackbar persists).
      name: 'rule_suggestion_nudge_dismissed';
      properties: { invoice_id: string };
    }
  | {
      // The conversion: the consultant added a suggested review rule to the
      // contract from the chat card. invoice_id / session_id are best-effort
      // attribution back to the originating invoice review.
      name: 'rule_suggestion_rule_added';
      properties: {
        contract_id: string;
        category:
          | 'review-rule'
          | 'included-service'
          | 'excluded-service'
          | 'miscellaneous-charge'
          | 'other-term';
        rule_count_in_card: number;
        rule_index: number;
        had_conflicts: boolean;
        invoice_id?: string;
        session_id?: string;
      };
    }
  | {
      // The add-rule API call failed (separates "never tried" from "API
      // rejected" in the conversion denominator).
      name: 'rule_suggestion_add_failed';
      properties: {
        contract_id: string;
        category:
          | 'review-rule'
          | 'included-service'
          | 'excluded-service'
          | 'miscellaneous-charge'
          | 'other-term';
        error_code?: `http_${number}` | 'unknown_error';
      };
    }
  | {
      // A non-empty rule-suggestion card actually painted (true card->add
      // denominator; chat_tool_result_rendered fires on tool return, which can
      // be an empty card).
      name: 'rule_suggestion_card_shown';
      properties: {
        contract_id: string;
        suggested_rule_count: number;
        conflicting_rule_count: number;
        session_id?: string;
      };
    }
  // An invoice approved from the extraction reviewer as an adjustment of an
  // existing one. `path` says how the reviewer got there: "Proceed as Adjusted
  // Invoice" in the duplicate dialog, or the form's "Original invoice" picker.
  // `invoice_id` is the new adjustment, `parent_invoice_id` the original.
  | {
      name: 'invoice_adjustment_saved';
      properties: {
        upload_id: string;
        document_id?: string;
        path: 'duplicate_dialog' | 'original_picker';
        invoice_id: string;
        parent_invoice_id: string;
      };
    }
  | {
      name: 'invoice_ai_review_initiated';
      properties: { invoice_id: string; trigger: 'auto' | 'manual' | 'retry' };
    }
  | {
      name: 'invoice_ai_review_feedback';
      properties: { invoice_id: string; helpful: boolean };
    }
  | {
      name: 'invoice_duplicate_warning_shown';
      properties: { invoice_id?: string };
    }
  | {
      name: 'invoice_duplicate_warning_overridden';
      properties: { invoice_id?: string };
    }
  | {
      name: 'invoice_bulk_action_completed';
      properties: { action: string; count: number };
    }
  | {
      name: 'invoice_tab_switched';
      properties: { invoice_id: string; tab: string };
    }
  | {
      name: 'invoice_note_added';
      properties: { invoice_id: string; note_length_bucket: 'xs' | 's' | 'm' | 'l' | 'xl' };
    }

  // ─── Dispute Agent ─────────────────────────────────────────────────
  // BE state machine: none → active ↔ awaiting-vendor → resolved/withdrawn
  | {
      name: 'dispute_panel_viewed';
      properties: { invoice_id: string; status: string };
    }
  | {
      name: 'dispute_suggestion_clicked';
      properties: {
        invoice_id: string;
        action: string;
        priority: 'primary' | 'secondary' | 'overflow';
      };
    }
  | {
      name: 'dispute_initiated';
      properties: { invoice_id: string };
    }
  | {
      name: 'dispute_draft_generated';
      properties: {
        invoice_id: string;
        draft_type: 'dispute' | 'data_request' | 'follow_up';
      };
    }
  | {
      name: 'dispute_draft_edited';
      properties: {
        invoice_id: string;
        fields: Array<'subject' | 'body' | 'to' | 'cc'>;
      };
    }
  | {
      name: 'dispute_draft_discarded';
      properties: { invoice_id: string };
    }
  | {
      name: 'dispute_email_sent';
      properties: {
        invoice_id: string;
        attachment_count: number;
        agent_generated: boolean;
      };
    }
  | {
      // Same posture as chat_error_displayed but with a tighter type:
      // the only call site mints either `http_<status>` from a
      // LiftAPIClientError or the literal 'unknown_error', so we encode
      // that exactly. Template literal types accept `http_400` etc. and
      // reject any other string at compile time.
      name: 'dispute_email_send_failed';
      properties: { invoice_id: string; error_code?: `http_${number}` | 'unknown_error' };
    }
  | {
      name: 'dispute_response_categorized';
      properties: {
        invoice_id: string;
        vendor_position:
          | 'agreed'
          | 'partial-agreement'
          | 'disagreed-with-evidence'
          | 'disagreed-without-evidence'
          | 'requested-more-info'
          | string;
      };
    }
  | {
      name: 'dispute_adjusted_invoice_review_clicked';
      properties: { invoice_id: string; adjusted_invoice_id: string };
    }
  | {
      name: 'dispute_resolved';
      properties: {
        invoice_id: string;
        resolution_type:
          | 'credit-issued'
          | 'adjusted-invoice-received'
          | 'vendor-rejected'
          | 'withdrawn'
          | 'other'
          | string;
        had_settled_amount: boolean;
      };
    }
  | {
      name: 'dispute_withdrawn';
      properties: { invoice_id: string; had_reason: boolean };
    }
  | {
      name: 'dispute_reopened';
      properties: { invoice_id: string; from_status: 'resolved' | 'withdrawn' | string };
    }

  // ─── Compliance ────────────────────────────────────────────────────
  // Route-level visibility (e.g. opening /compliance) is covered by
  // page_viewed — no separate compliance_viewed event.
  | {
      name: 'compliance_item_opened';
      properties: { entity_type: 'car' | 'certificate' | 'violation'; entity_id: string };
    }
  | {
      name: 'certificate_uploaded';
      properties: { status: 'success' | 'error'; count?: number };
    }

  // ─── Tickets ───────────────────────────────────────────────────────
  // Route-level visibility covered by page_viewed.
  | { name: 'ticket_opened'; properties: { ticket_id: string } }
  | {
      name: 'ticket_csv_import_completed';
      properties: { row_count: number; success_count: number; error_count: number };
    }
  | {
      // Once per opened panel, not per row stepped through.
      name: 'list_preview_opened';
      properties: {
        list: PreviewList;
        trigger: 'document_link' | 'peek_hover' | 'keyboard';
        record_id: string;
      };
    }
  | {
      name: 'list_peek_mode_toggled';
      properties: { list: PreviewList; enabled: boolean };
    }
  | {
      name: 'table_export_initiated';
      properties: { table: 'tickets' | 'spending' | string };
    }

  // ─── Insights ──────────────────────────────────────────────────────
  // Route-level visibility covered by page_viewed.
  | { name: 'insight_template_opened'; properties: { template_id: string; account_ids: string[] } }
  | { name: 'insight_custom_request_initiated'; properties: Record<string, never> }
  | { name: 'insight_report_copied'; properties: { report_id?: string } }

  // ─── Dashboards (live reports) ─────────────────────────────────────
  // Kept distinct from the legacy insight_* events so live-dashboard
  // behavior stays separable from frozen-report behavior in every query.
  // The live report is a single, stateless render over a set of portfolios (no
  // persisted dashboard), so these carry account ids / block position, not ids.
  //
  // `account_ids` is a list because the report covers the portfolios a caller
  // can reach, which can span several of their customer accounts. It was a
  // single `account_id` back when the report rendered one arbitrarily-chosen
  // account; weekly-viewer counts sliced by that field change meaning at the
  // point this shipped.
  | {
      name: 'dashboard_opened';
      properties: { account_ids: string[] };
    }
  | {
      name: 'dashboard_block_interacted';
      properties: {
        block_position: number;
        block_type: string;
        interaction: 'expand' | 'filter' | 'tooltip';
      };
    }
  // Narrowing the report to a portfolio or region. Its own event, not another
  // `dashboard_opened`: filtering used to re-fire the open, which inflated
  // opens and made filtering invisible. For an experiment measuring whether
  // people engage with the report, "did they narrow it to their own portfolio"
  // is arguably the more useful of the two signals.
  | {
      name: 'dashboard_filtered';
      properties: { portfolio_count: number; region_count: number };
    }
  | {
      name: 'dashboard_compared';
      properties: { axis: 'period' | 'property' | 'region' | 'vendor' };
    }
  // Which of the two Reports tabs — the live report or the frozen archive —
  // users actually move between.
  | {
      name: 'reports_tab_switched';
      properties: { tab: string };
    }

  // ─── Portfolio ─────────────────────────────────────────────────────
  // Route-level visibility covered by page_viewed.
  | { name: 'property_opened'; properties: { property_id: string } }
  | {
      name: 'property_tab_switched';
      properties: { property_id: string; tab: string };
    }
  | {
      name: 'global_filter_applied';
      properties: {
        portfolio_count: number;
        service_provider_count: number;
        region_count: number;
      };
    }
  | {
      name: 'contract_accordion_expanded';
      properties: { property_id: string; contract_id: string; expanded: boolean };
    }
  | {
      name: 'admin_tab_switched';
      properties: { tab: string };
    }

  // ─── Document Review (multi-step funnel) ───────────────────────────
  // No `document_upload_completed` — the frontend doesn't observe the
  // backend's pipeline finish; document_review_opened on the next row
  // click is the user-side moment that matters. No
  // `document_pipeline_status_changed` either — those transitions are
  // server-driven and surfaced via the upload's status field on the
  // next list refresh, not as discrete client events.
  | { name: 'document_review_viewed'; properties: { tab?: 'uploads' | 'emails' } }
  | {
      name: 'document_upload_initiated';
      properties: { source: 'manual' | 'email'; file_count: number };
    }
  | {
      name: 'document_review_opened';
      properties: { upload_id: string };
    }
  | {
      name: 'document_reprocess_initiated';
      properties: { upload_id: string };
    }
  | {
      name: 'document_extraction_action';
      properties: {
        upload_id: string;
        document_id?: string;
        action: 'accepted' | 'rejected' | 'reclassified' | 'retry_extraction' | 'flagged';
        classification?: string;
      };
    }
  // The reviewer's "Duplicate Invoice Detected" dialog (not the manual form's
  // `invoice_duplicate_warning_*`, which are a separate surface). `shown` is the
  // denominator for how often reviewers adjust an invoice rather than override
  // or cancel it.
  | {
      name: 'document_duplicate_warning_shown';
      properties: { upload_id: string; document_id?: string };
    }
  | {
      name: 'document_duplicate_warning_resolved';
      properties: {
        upload_id: string;
        document_id?: string;
        resolution: 'cancelled' | 'proceed_anyway' | 'proceed_as_adjusted';
      };
    }
  | {
      name: 'document_email_archived';
      properties: { email_id: string; archived: boolean };
    }
  | { name: 'document_email_opened'; properties: { email_id: string } }

  // ─── Document split-review (boundary triage gate) ──────────────────
  // The human-in-the-loop step that pauses multi-page uploads at
  // AWAITING_SPLIT_REVIEW before classify/extract (AMP-286). Single-page
  // and contract-hint uploads bypass it entirely, so these events fire
  // only on the multi-page path.
  | {
      // Workbench mount, ref-deduped per upload. `proposed_document_count`
      // is the count of cuts the splitter produced (= boundaries.length);
      // `flagged_seam_count` is how many of those were low-confidence.
      name: 'document_split_review_opened';
      properties: {
        upload_id: string;
        proposed_document_count: number;
        flagged_seam_count: number;
      };
    }
  | {
      // Fires once per user-driven boundary edit (split/merge/nudge).
      // Counts undone edits too — represents the reviewer's edit volume,
      // not the net delta. Use `count() / count(approved)` for per-upload
      // edit intensity as a splitter-accuracy signal.
      name: 'document_split_review_correction';
      properties: {
        upload_id: string;
        correction_type: 'split' | 'merge' | 'nudge';
      };
    }
  | {
      // Fires after the Approve API succeeds (not on 4xx). `correction_count`
      // is the cumulative split+merge+nudge actions in this session;
      // `soft_guard_warned` is true if the reviewer bypassed the
      // unconfirmed-flag / unviewed-multi-page warning to approve anyway.
      name: 'document_split_review_approved';
      properties: {
        upload_id: string;
        final_document_count: number;
        correction_count: number;
        soft_guard_warned: boolean;
      };
    }

  // ─── Support / Docs & Guides ───────────────────────────────────────
  // Fired from the support dialog and the Docs & Guides link. The
  // `category` enum mirrors the support-request categories on submit.
  | { name: 'support_request_opened'; properties: Record<string, never> }
  | {
      name: 'support_request_submitted';
      properties: {
        category: 'question' | 'problem' | 'feature_request' | 'billing';
      };
    }
  // Fired when the support relay POST fails. The relay is stateless, so a
  // failure means the request was lost — this is the only analytics signal it
  // was attempted-but-not-recorded, and lets the funnel distinguish a system
  // failure from user abandonment. `status` is the HTTP status when available
  // (absent for network errors).
  | { name: 'support_request_failed'; properties: { status?: number } }
  // Docs & Guides (formerly Help Center), opened from the account menu or What's New.
  | { name: 'help_center_clicked'; properties: { source: 'account_menu' | 'whats_new' } }

  // ─── What's New (changelog) ────────────────────────────────────────
  // Fired from the "What's New" bell and the bottom-left card. `unread_count` is the number
  // of entries newer than the user's seen date at the moment the panel opens.
  | { name: 'whats_new_opened'; properties: { unread_count: number } }
  | {
      name: 'whats_new_entry_clicked';
      properties: { entry_id: string; source: 'bell' | 'card' | 'showcase' };
    }
  | { name: 'whats_new_card_dismissed'; properties: { unread_count: number } }
  // The showcase dialog for updates with a demo video. `auto` opens on its own for
  // unread updates; `bell` is a replay from the What's New panel.
  | {
      name: 'whats_new_showcase_shown';
      properties: { entry_count: number; source: 'auto' | 'bell' };
    }
  | {
      name: 'whats_new_showcase_closed';
      properties: {
        entry_id: string;
        position: number;
        total: number;
        finished: boolean;
        source: 'auto' | 'bell';
      };
    }
  // ─── Contract Favorability Index (CFI) ─────────────────────────────
  // AI-scored contract review with a consultant-in-the-loop edit/publish
  // flow (status: pending → running → awaiting_review → published, with a
  // reopen path back from published, plus failed/superseded). Two funnels
  // hang off these events: consultant activation
  // (cfi_tab_viewed → cfi_run_initiated → cfi_run_completed →
  // cfi_report_published) and client engagement
  // (cfi_tab_viewed[client] → cfi_report_opened[published]).
  // `cfi_finding_edited.overrode_agent_rating` is the model-trust signal —
  // how often consultants change the agent's rating. No contract text,
  // rating narratives, or scores travel as properties: findings are
  // referenced by id and dispositions are low-cardinality enums/counts.
  | {
      // Tab mount, ref-deduped per contract. `report_status` is the latest
      // report's status at first view ('none' when no run exists yet) — the
      // funnel entry point and denominator for both viewer roles.
      name: 'cfi_tab_viewed';
      properties: {
        contract_id: string;
        viewer_role: 'consultant' | 'client';
        report_status:
          | 'none'
          | 'pending'
          | 'running'
          | 'failed'
          | 'awaiting_review'
          | 'published'
          | 'superseded';
      };
    }
  | {
      // Consultant clicked Run / Retry. `trigger` is 'retry' when the prior
      // report for this contract was in the failed state.
      name: 'cfi_run_initiated';
      properties: { contract_id: string; trigger: 'initial' | 'retry' };
    }
  | {
      // Fired when the poll observes the run reach awaiting_review live (the
      // tab was open across the transition). A report already awaiting
      // review on first load does NOT fire this — same posture as document
      // review: the frontend only logs runs the user actually watched.
      name: 'cfi_run_completed';
      properties: { contract_id: string; report_id: string; finding_count: number };
    }
  | {
      // Run reached the failed state while observed. Failure forensics live
      // in the backend logs / Sentry; the analytics signal is just the count.
      name: 'cfi_run_failed';
      properties: { contract_id: string; report_id: string };
    }
  | {
      // One consultant edit to a finding, fired after the PATCH succeeds.
      // `fields` is which editable fields changed in this save;
      // `overrode_agent_rating` is true when the saved rating differs from
      // the agent's original (always false for consultant-custom findings,
      // which the agent never scored) — the model-trust signal.
      name: 'cfi_finding_edited';
      properties: {
        report_id: string;
        finding_source: 'core' | 'ai_discovered' | 'consultant_custom';
        fields: Array<'rating' | 'summary' | 'recommended_action'>;
        overrode_agent_rating: boolean;
      };
    }
  | {
      // Consultant excluded a finding from the report. Undrop is not tracked.
      name: 'cfi_finding_dropped';
      properties: {
        report_id: string;
        finding_source: 'core' | 'ai_discovered' | 'consultant_custom';
      };
    }
  | {
      // Consultant authored a finding outside the rubric. `rating` is the
      // disposition they assigned (no title / rationale text travels).
      name: 'cfi_custom_finding_added';
      properties: {
        report_id: string;
        rating: 'favorable' | 'neutral' | 'unfavorable' | 'not_applicable';
      };
    }
  | {
      // Report finalized for the client. `republish` is true when a
      // previously-published report was reopened, edited, and re-published.
      // Counts are the final per-rating disposition (small, non-sensitive).
      name: 'cfi_report_published';
      properties: {
        report_id: string;
        finding_count: number;
        unfavorable_count: number;
        republish: boolean;
      };
    }
  | {
      // Published report reverted to awaiting_review for further edits.
      name: 'cfi_report_reopened';
      properties: { report_id: string };
    }
  | {
      // Rendered HTML report opened in a new tab. `mode` separates the
      // consultant's pre-publish preview from views of the published report
      // (the client-engagement signal when fired from the client view).
      name: 'cfi_report_opened';
      properties: { report_id: string; mode: 'preview' | 'published' };
    };

export type AppEventName = AppEvent['name'];

export type AppEventProperties<N extends AppEventName> = Extract<
  AppEvent,
  { name: N }
>['properties'];

// Convert a character count into a coarse bucket — never send raw message
// content to Mixpanel, only the length category. Bands chosen for the
// chat surface; reuse for any user-authored text input we want to size.
export function bucketLength(text: string): 'xs' | 's' | 'm' | 'l' | 'xl' {
  const n = text.length;
  if (n < 20) return 'xs';
  if (n < 80) return 's';
  if (n < 200) return 'm';
  if (n < 600) return 'l';
  return 'xl';
}
